import { CometChatCalls } from '@cometchat/calls-sdk-javascript';
import { useHotkey } from '@tanstack/react-hotkeys';
import { useEffect, useState } from 'react';

/**
 * Google Meet–style keyboard shortcuts using TanStack Hotkeys.
 * Returns `showShortcuts` state + setter so the consumer can render the dialog.
 */
export function useKeyboardShortcuts(isSessionActive: boolean) {
  const [showShortcuts, setShowShortcuts] = useState(false);

  // Shift+/ (i.e. "?") — toggle shortcuts dialog
  // Manual listener because TanStack Hotkeys' strict modifier matching
  // doesn't support Shift+PunctuationKey (Shift changes the key value).
  useEffect(() => {
    if (!isSessionActive) return;

    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const isEditable =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        (e.target as HTMLElement)?.isContentEditable;

      if (isEditable) return;

      if (e.key === '?' && !e.ctrlKey && !e.altKey && !e.metaKey) {
        e.preventDefault();
        setShowShortcuts(prev => !prev);
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isSessionActive]);

  // Mod+D — Toggle audio (Mod = ⌘ on Mac, Ctrl on Windows)
  useHotkey('Mod+D', () => CometChatCalls.toggleAudio(), {
    enabled: isSessionActive,
  });

  // Mod+E — Toggle video
  useHotkey('Mod+E', () => CometChatCalls.toggleVideo(), {
    enabled: isSessionActive,
  });

  // Ctrl+⌘+H / Ctrl+Alt+H — Raise or lower hand
  useHotkey('Control+Meta+H', () => CometChatCalls.toggleHand(), {
    enabled: isSessionActive,
  });

  // Ctrl+⌘+P / Ctrl+Alt+P — Toggle participant list
  useHotkey('Control+Meta+P', () => CometChatCalls.toggleParticipantList(), {
    enabled: isSessionActive,
  });

  return { showShortcuts, setShowShortcuts };
}
